import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaEdit,
  FaFilePdf,
  FaEnvelope,
  FaCheck,
  FaPaperPlane,
  FaFileInvoiceDollar,
  FaClipboardCheck,
  FaTasks,
  FaExclamationTriangle,
  FaChevronDown,
  FaEye,
  FaFileContract,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import OfferService, { Offer, PaymentPlan, PaymentMilestone } from '../services/OfferService';
import InvoiceService from '../services/InvoiceService';
import ProjectService from '../../contacts/services/ProjectService';
import BusinessPartnerService, { BusinessPartner } from '../../contacts/services/BusinessPartnerService';
import SendOfferEmailModal from './SendOfferEmailModal';
import CreateInvoiceFromOfferModal from './CreateInvoiceFromOfferModal';
import CreateServiceContractModal from './CreateServiceContractModal';
import AcceptOfferModal from './AcceptOfferModal';
import OrderConfirmationModal from './OrderConfirmationModal';
import MilestoneInvoiceModal from './MilestoneInvoiceModal';
import MilestoneTasksModal from './MilestoneTasksModal';
import OfferLineScopeDrawer from './OfferLineScopeDrawer';
import { PdfDrawer } from './OfferPdfDrawer';
import { StatusDropdown } from './OfferStatusDropdown';
import { ActionDropdown } from './OfferActionDropdown';
import { WorkflowTimeline, WorkflowStep } from './OfferWorkflowTimeline';
import styles from './OfferDetailPage.module.css';

export default function OfferDetailPage(): React.JSX.Element {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [partner, setPartner] = useState<BusinessPartner | null>(null);
  const [projectName, setProjectName] = useState('');
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPdfDrawer, setShowPdfDrawer] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceModalKind, setInvoiceModalKind] = useState<'FULL' | 'DEPOSIT'>('FULL');
  const [showServiceContractModal, setShowServiceContractModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showOrderConfirmationModal, setShowOrderConfirmationModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [showMilestoneInvoiceModal, setShowMilestoneInvoiceModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<PaymentMilestone | null>(null);
  const [showMilestoneTasksModal, setShowMilestoneTasksModal] = useState(false);
  const [tasksMilestone, setTasksMilestone] = useState<PaymentMilestone | null>(null);
  const [showOpenTasksWarning, setShowOpenTasksWarning] = useState(false);
  const [pendingInvoiceMilestone, setPendingInvoiceMilestone] = useState<PaymentMilestone | null>(null);
  const [statusDropdownMilestoneId, setStatusDropdownMilestoneId] = useState<number | null>(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const [scopeDrawerOpen, setScopeDrawerOpen] = useState(false);
  const [scopeDrawerLine, setScopeDrawerLine] = useState<{ id: number; name: string } | null>(null);

  const loadOffer = useCallback(async () => {
    if (!offerId) return;
    setLoading(true);
    try {
      const offerData = await OfferService.get(parseInt(offerId, 10));
      setOffer(offerData);
      if (offerData.business_partner_id) {
        try {
          const partnerData = await BusinessPartnerService.get(offerData.business_partner_id);
          setPartner(partnerData);
        } catch { setPartner(null); }
      }
      if (offerData.project_id) {
        try {
          const projectData = await ProjectService.get(offerData.project_id);
          setProjectName(projectData.name);
        } catch { setProjectName(offerData.project_name || ''); }
      }
      try {
        const plan = await OfferService.getPaymentPlan(parseInt(offerId, 10));
        setPaymentPlan(plan);
      } catch { setPaymentPlan(null); }
    } catch (err) {
      console.error('Failed to load offer:', err);
      toast.error('Failed to load offer');
      navigate('/sales');
    } finally { setLoading(false); }
  }, [offerId, navigate]);

  useEffect(() => { loadOffer(); }, [loadOffer]);
  useEffect(() => { return () => { if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl); }; }, [pdfBlobUrl]);

  const handleBack = () => navigate('/sales');
  const handleEdit = () => { if (offer) navigate(`/offers/${offer.id}/edit`); };

  const handleLoadPdf = async () => {
    if (!offer) return;
    setShowPdfDrawer(true); setLoadingPdf(true);
    setPdfBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    try {
      await OfferService.renderPdf(offer.id);
      const blob = await OfferService.getPdfBlob(offer.id);
      setPdfBlobUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error('Failed to load PDF:', err); toast.error('Failed to load PDF'); setShowPdfDrawer(false);
    } finally { setLoadingPdf(false); }
  };

  const handleDownloadPdf = async () => {
    if (!offer) return;
    try {
      const blob = await OfferService.downloadPdf(offer.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `Angebot_${offer.offer_number}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Failed to download PDF:', err); toast.error('Failed to download PDF'); }
  };

  const handleDownloadOrderConfirmationPdf = async () => {
    if (!offer || !offer.order_confirmation_number) return;
    try {
      const blob = await OfferService.getOrderConfirmationPdfBlob(offer.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `${offer.order_confirmation_number}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Failed to download order confirmation PDF:', err); toast.error('AB-PDF konnte nicht heruntergeladen werden'); }
  };

  const handleViewOrderConfirmationPdf = async () => {
    if (!offer || !offer.order_confirmation_number) return;
    setShowPdfDrawer(true); setLoadingPdf(true);
    setPdfBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    try {
      const blob = await OfferService.getOrderConfirmationPdfBlob(offer.id);
      setPdfBlobUrl(URL.createObjectURL(blob));
    } catch (err) { console.error('Failed to load order confirmation PDF:', err); toast.error('Failed to load PDF'); setShowPdfDrawer(false); }
    finally { setLoadingPdf(false); }
  };

  const handleViewInvoicePdf = async (invoiceId: number) => {
    setShowPdfDrawer(true); setLoadingPdf(true);
    setPdfBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    try {
      const blob = await InvoiceService.downloadPdf(invoiceId);
      setPdfBlobUrl(URL.createObjectURL(blob));
    } catch (err) { console.error('Failed to load invoice PDF:', err); toast.error('Failed to load PDF'); setShowPdfDrawer(false); }
    finally { setLoadingPdf(false); }
  };

  const handleAcceptWithNote = async (confirmationNote: string) => {
    if (!offer) return;
    setActionLoading(true);
    try {
      const result = await OfferService.accept(offer.id);
      toast.success('Angebot als akzeptiert markiert');
      if (result.project_status_updated) toast.info('Projektstatus auf ORDER_CONFIRMED aktualisiert');
      setShowAcceptModal(false); loadOffer();
    } catch (err) { console.error('Failed to accept offer:', err); toast.error('Status konnte nicht aktualisiert werden'); }
    finally { setActionLoading(false); }
  };

  const handleOpenMilestoneInvoiceModal = (milestone: PaymentMilestone) => {
    const openTasksCount = milestone.tasks_open ?? 0;
    if (openTasksCount > 0) { setPendingInvoiceMilestone(milestone); setShowOpenTasksWarning(true); }
    else { setSelectedMilestone(milestone); setShowMilestoneInvoiceModal(true); }
  };

  const handleConfirmInvoiceWithOpenTasks = () => {
    if (pendingInvoiceMilestone) { setSelectedMilestone(pendingInvoiceMilestone); setShowMilestoneInvoiceModal(true); }
    setShowOpenTasksWarning(false); setPendingInvoiceMilestone(null);
  };

  const handleChangeMilestoneStatus = async (milestoneId: number, newStatus: 'PLANNED' | 'READY_FOR_INVOICE' | 'CANCELLED') => {
    setChangingStatus(true);
    try {
      await OfferService.changeMilestoneStatus(milestoneId, newStatus);
      toast.success('Milestone status updated'); loadOffer();
    } catch (err: any) {
      console.error('Failed to change milestone status:', err);
      toast.error(err?.response?.data?.detail || 'Failed to change status');
    } finally { setChangingStatus(false); setStatusDropdownMilestoneId(null); }
  };

  const handleOpenInvoiceModal = (kind: 'FULL' | 'DEPOSIT') => { setInvoiceModalKind(kind); setShowInvoiceModal(true); };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) return <div className={styles.loadingContainer}>Loading...</div>;
  if (!offer) return <div className={styles.loadingContainer}>Offer not found</div>;

  const normalizedStatus = (offer.status || '').toUpperCase();
  const statusColor = OfferService.getStatusColor(offer.status);
  const partnerName = partner ? BusinessPartnerService.getDisplayName(partner) : offer.business_partner_name || '-';
  const canEdit = normalizedStatus === 'DRAFT';
  const isSent = ['SENT', 'ACCEPTED', 'REJECTED', 'INVOICED'].includes(normalizedStatus);
  const isAccepted = ['ACCEPTED', 'INVOICED'].includes(normalizedStatus);
  const hasAB = !!offer.order_confirmation_number;
  const isInvoiced = normalizedStatus === 'INVOICED';

  console.log('Offer Debug:', { rawStatus: offer.status, normalizedStatus, isSent, isAccepted, isInvoiced, hasAB });

  const buildWorkflowSteps = (): WorkflowStep[] => {
    const baseSteps: WorkflowStep[] = [
      { id: 'created', actionLabel: 'Erstellen', completedLabel: 'Erstellt', completed: true, icon: <FaFilePdf size={18} color="#fff" />, completedAt: offer.created_at, onCompletedClick: () => handleLoadPdf() },
      { id: 'sent', actionLabel: 'Versenden', completedLabel: 'Versendet', completed: isSent, onClick: () => setShowEmailModal(true), onCompletedClick: () => setShowEmailModal(true), disabled: !isSent && normalizedStatus !== 'DRAFT', icon: <FaPaperPlane size={18} color={isSent ? '#fff' : '#999'} />, completedAt: offer.sent_at },
      { id: 'accepted', actionLabel: 'Akzeptieren', completedLabel: 'Akzeptiert', completed: isAccepted, onClick: () => setShowAcceptModal(true), disabled: !isAccepted && normalizedStatus !== 'SENT', icon: <FaCheck size={18} color={isAccepted ? '#fff' : '#999'} />, completedAt: offer.accepted_at },
      { id: 'ab', actionLabel: 'AB senden', completedLabel: 'AB gesendet', completed: hasAB, onClick: () => setShowOrderConfirmationModal(true), onCompletedClick: () => handleDownloadOrderConfirmationPdf(), disabled: !hasAB && !isAccepted, show: isAccepted || hasAB, icon: <FaClipboardCheck size={18} color={hasAB ? '#fff' : '#999'} />, completedAt: offer.order_confirmed_at },
    ];

    if (paymentPlan && paymentPlan.milestones.length > 0) {
      const totalMilestones = paymentPlan.milestones.length;
      const invoicedMilestones = paymentPlan.milestones.filter((m) => m.invoice_id || m.status === 'INVOICED' || m.status === 'PAID');
      const invoicedCount = invoicedMilestones.length;
      const allInvoiced = invoicedCount === totalMilestones;
      const hasAnyInvoice = invoicedCount > 0;
      const milestonesWithDates = invoicedMilestones.filter((m) => m.invoiced_at);
      const lastInvoicedDate = milestonesWithDates.length > 0
        ? milestonesWithDates.sort((a, b) => new Date(b.invoiced_at!).getTime() - new Date(a.invoiced_at!).getTime())[0].invoiced_at
        : undefined;
      const nextMilestoneToInvoice = paymentPlan.milestones.find((m) => !m.invoice_id && m.status !== 'INVOICED' && m.status !== 'PAID' && m.status !== 'CANCELLED');

      baseSteps.push({
        id: 'invoice',
        actionLabel: nextMilestoneToInvoice ? `${nextMilestoneToInvoice.label} (${invoicedCount}/${totalMilestones})` : `Rechnung (${invoicedCount}/${totalMilestones})`,
        completedLabel: allInvoiced ? `Alle Rechnungen (${totalMilestones}/${totalMilestones})` : `${invoicedCount}/${totalMilestones} Rechnungen`,
        completed: allInvoiced,
        onClick: nextMilestoneToInvoice ? () => handleOpenMilestoneInvoiceModal(nextMilestoneToInvoice) : undefined,
        onCompletedClick: allInvoiced ? undefined : (nextMilestoneToInvoice ? () => handleOpenMilestoneInvoiceModal(nextMilestoneToInvoice) : undefined),
        disabled: !hasAB && !hasAnyInvoice,
        show: isAccepted || hasAB || hasAnyInvoice,
        icon: <FaFileInvoiceDollar size={18} color={allInvoiced || hasAnyInvoice ? '#fff' : '#999'} />,
        completedAt: allInvoiced ? (offer.invoiced_at || lastInvoicedDate) : undefined,
        partialProgress: hasAnyInvoice && !allInvoiced ? `${invoicedCount}/${totalMilestones}` : undefined,
        partialProgressDate: hasAnyInvoice && !allInvoiced ? lastInvoicedDate : undefined,
      });
    } else {
      baseSteps.push({
        id: 'invoice', actionLabel: 'Rechnung', completedLabel: 'Rechnung erstellt', completed: isInvoiced,
        onClick: () => handleOpenInvoiceModal('FULL'), disabled: !hasAB && !isInvoiced, show: isAccepted || hasAB || isInvoiced,
        icon: <FaFileInvoiceDollar size={18} color={isInvoiced ? '#fff' : '#999'} />, completedAt: offer.invoiced_at,
      });
    }
    return baseSteps;
  };

  return (
    <div className={styles.pageContainer}>
      <button onClick={handleBack} className={styles.backBtn}>
        <FaArrowLeft size={14} /> Back to Sales
      </button>

      <div className={styles.headerCard}>
        <div className={styles.headerTopRow}>
          <div>
            <div className={styles.headerTitleRow}>
              <h1 className={styles.headerTitle}>{offer.title}</h1>
              <StatusDropdown
                currentStatus={normalizedStatus} statusColor={statusColor}
                statusLabel={OfferService.getStatusLabel(offer.status)}
                onChangeStatus={async (newStatus) => {
                  setActionLoading(true);
                  try {
                    if (newStatus === 'SENT') { await OfferService.changeStatus(offer.id, 'SENT'); toast.success('Angebot als versendet markiert'); }
                    else if (newStatus === 'ACCEPTED') { setShowAcceptModal(true); setActionLoading(false); return; }
                    else if (newStatus === 'REJECTED') { await OfferService.changeStatus(offer.id, 'REJECTED'); toast.success('Angebot als abgelehnt markiert'); }
                    loadOffer();
                  } catch (err) { console.error('Failed to change status:', err); toast.error('Status konnte nicht geaendert werden'); }
                  finally { setActionLoading(false); }
                }}
                disabled={actionLoading}
              />
            </div>
            <p className={styles.headerMeta}>Nr.: {offer.offer_number}</p>
            <p className={styles.headerMeta}>
              <strong>Kunde:</strong>{' '}
              {offer.business_partner_id ? (
                <span onClick={() => navigate(`/contacts/${offer.business_partner_id}`)} className={styles.headerLink}>{partnerName}</span>
              ) : (<span>{partnerName}</span>)}
            </p>
            <p className={styles.headerMeta}>
              <strong>Projekt:</strong>{' '}
              {offer.project_id ? (
                <span onClick={() => navigate(`/projects/${offer.project_id}`)} className={styles.headerLink}>{projectName || '-'}</span>
              ) : (<span className={styles.headerMuted}>--</span>)}
            </p>
            {offer.order_confirmation_number && (
              <p className={styles.headerOrderConfirmation}>
                Auftragsbestaetigung: {offer.order_confirmation_number}
                {offer.order_confirmed_at && (<span className={styles.headerOrderConfirmationDate}> ({formatDate(offer.order_confirmed_at)})</span>)}
              </p>
            )}
          </div>
          <div className={styles.headerAmount}>
            <div className={styles.headerAmountValue}>{OfferService.formatCurrency(offer.total_gross)}</div>
            <div className={styles.headerValidUntil}>Gueltig bis: {formatDate(offer.valid_until)}</div>
          </div>
        </div>

        <div className={styles.actionsRow}>
          {canEdit && (<button onClick={handleEdit} className={styles.actionBtn}><FaEdit size={14} /> Bearbeiten</button>)}
          <ActionDropdown label="View" icon={<FaEye size={14} />} items={[
            { label: 'Offer PDF', icon: <FaFilePdf size={14} />, onClick: handleLoadPdf, disabled: loadingPdf },
            { label: 'Order Confirmation PDF', icon: <FaClipboardCheck size={14} />, onClick: handleViewOrderConfirmationPdf, show: !!offer.order_confirmation_number, disabled: loadingPdf },
            { label: 'Invoice PDF', icon: <FaFileInvoiceDollar size={14} />, onClick: () => { if (paymentPlan && paymentPlan.milestones.length > 0) { const m = paymentPlan.milestones.find((m) => m.invoice_id); if (m?.invoice_id) handleViewInvoicePdf(m.invoice_id); } }, show: paymentPlan?.milestones.some((m) => m.invoice_id) || isInvoiced, disabled: loadingPdf },
          ]} />
          <ActionDropdown label="Send" icon={<FaPaperPlane size={14} />} items={[
            { label: 'Send Offer', icon: <FaFilePdf size={14} />, onClick: () => setShowEmailModal(true) },
            { label: 'Send Order Confirmation', icon: <FaClipboardCheck size={14} />, onClick: () => setShowOrderConfirmationModal(true), show: isAccepted, disabled: actionLoading },
            { label: 'Create Invoice', icon: <FaFileInvoiceDollar size={14} />, onClick: () => { if (paymentPlan && paymentPlan.milestones.length > 0) { const nm = paymentPlan.milestones.find((m) => !m.invoice_id && m.status !== 'INVOICED' && m.status !== 'PAID' && m.status !== 'CANCELLED'); if (nm) handleOpenMilestoneInvoiceModal(nm); } else handleOpenInvoiceModal('FULL'); }, show: isAccepted, disabled: actionLoading },
            { label: 'Send Invoice', icon: <FaEnvelope size={14} />, onClick: () => { if (paymentPlan && paymentPlan.milestones.length > 0) { const m = paymentPlan.milestones.find((m) => m.invoice_id); if (m?.invoice_id) navigate(`/invoices/${m.invoice_id}`); } }, show: isAccepted && paymentPlan?.milestones.some((m) => m.invoice_id), disabled: actionLoading },
          ]} />
          <button onClick={() => setShowServiceContractModal(true)} disabled={actionLoading || !isAccepted} className={styles.serviceContractBtn} style={{ opacity: !isAccepted ? 0.5 : 1, cursor: !isAccepted ? 'not-allowed' : 'pointer' }}>
            <FaFileContract size={14} /> Wartungsvertrag
          </button>
        </div>

        <WorkflowTimeline steps={buildWorkflowSteps()} />
      </div>

      {/* Line Items */}
      <div className={styles.contentCard}>
        <h3 className={styles.sectionTitle}>Line items</h3>
        {offer.line_items && offer.line_items.length > 0 ? (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead><tr className={styles.tableHead}>
                <th className={styles.th}>#</th><th className={styles.th}>Item</th>
                <th className={styles.thRight}>Qty</th><th className={styles.thRight}>Unit price</th>
                <th className={styles.thRight}>Total</th>
                {paymentPlan && <th className={styles.th}>Milestone</th>}
                <th className={styles.th} style={{ width: '60px' }}></th>
              </tr></thead>
              <tbody>
                {offer.line_items.map((item, index) => (
                  <tr key={item.id || index} className={styles.tableRow}>
                    <td className={styles.td}>{item.position}</td>
                    <td className={styles.td}>
                      <div className={styles.itemNameBold}>{item.name}</div>
                      {item.description && <div className={styles.itemDescription}>{item.description}</div>}
                    </td>
                    <td className={styles.tdRight}>{item.quantity} {item.unit}</td>
                    <td className={styles.tdRight}>{OfferService.formatCurrency(item.unit_price)}</td>
                    <td className={styles.tdBoldRight}>{OfferService.formatCurrency(item.total_net)}</td>
                    {paymentPlan && (<td className={styles.td}>
                      {item.payment_milestone_id ? (<span className={styles.milestoneBadge}>{paymentPlan.milestones.find(m => m.id === item.payment_milestone_id)?.label || `MS ${item.payment_milestone_id}`}</span>) : (<span className={styles.milestoneUnassigned}>Nicht zugeordnet</span>)}
                    </td>)}
                    <td className={styles.td}>{item.id && (<button data-testid={`scope-btn-${item.id}`} onClick={() => { setScopeDrawerLine({ id: item.id!, name: item.name }); setScopeDrawerOpen(true); }} className={styles.scopeBtn} title="Leistungsumfang bearbeiten">Scope</button>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={styles.totalsSection}>
              <div className={styles.totalRow}><span className={styles.totalLabel}>Subtotal net:</span><span className={styles.totalValue}>{OfferService.formatCurrency(offer.subtotal_net)}</span></div>
              <div className={styles.totalRow}><span className={styles.totalLabel}>VAT:</span><span className={styles.totalValue}>{OfferService.formatCurrency(offer.total_tax)}</span></div>
              <div className={styles.grandTotalRow}><span className={styles.grandTotalLabel}>Total gross:</span><span className={styles.grandTotalValue}>{OfferService.formatCurrency(offer.total_gross)}</span></div>
            </div>
          </div>
        ) : (<div className={styles.noLineItems}>No line items</div>)}
        {offer.terms && (<div className={styles.termsSection}><h4 className={styles.termsTitle}>Payment terms</h4><p className={styles.termsText}>{offer.terms}</p></div>)}
        {offer.notes && (<div className={styles.termsSection}><h4 className={styles.termsTitle}>Internal notes</h4><div className={styles.notesBox}><p className={styles.termsText}>{offer.notes}</p></div></div>)}
      </div>

      {/* Payment Plan */}
      {paymentPlan && paymentPlan.milestones.length > 0 && (
        <div className={styles.contentCardMarginTop}>
          <div className={styles.paymentPlanHeader}>
            <div className={styles.paymentPlanTitleRow}><FaFileInvoiceDollar size={20} color="#666" /><h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>Payment Plan</h3></div>
            <div className={styles.paymentPlanTotal}>Total: <strong>{OfferService.formatCurrency(paymentPlan.total_gross)}</strong><span style={{ marginLeft: '8px' }}>({paymentPlan.milestones.length} milestones)</span></div>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead><tr className={styles.tableHead}>
                <th className={styles.th} style={{ width: '40px' }}>#</th><th className={styles.th} style={{ width: '180px' }}>Label</th><th className={styles.th} style={{ width: '100px' }}>Type</th>
                <th className={styles.thRight} style={{ width: '50px' }}>%</th><th className={styles.thRight} style={{ width: '100px' }}>Amount</th><th className={styles.th} style={{ width: '100px' }}>Status</th>
                <th className={styles.th} style={{ width: '140px' }}>Invoice</th><th className={styles.thCenter} style={{ width: '140px' }}>Action</th><th className={styles.thCenter} style={{ width: '50px' }}>Tasks</th>
              </tr></thead>
              <tbody>
                {paymentPlan.milestones.map((milestone) => {
                  const isReadyForInvoice = milestone.status === 'READY_FOR_INVOICE';
                  const isDraft = milestone.invoice_id && !milestone.invoice_sent_at && milestone.status !== 'PAID';
                  const isPaid = milestone.status === 'PAID';
                  const getStatusDisplay = () => {
                    if (isPaid) return { label: 'Paid', bg: '#dcfce7', text: '#166534' };
                    if (isDraft) return { label: 'Draft', bg: '#fef3c7', text: '#92400e' };
                    if (milestone.invoice_id) return { label: 'Invoiced', bg: '#dbeafe', text: '#1e40af' };
                    if (isReadyForInvoice) return { label: 'Ready', bg: '#dcfce7', text: '#166534' };
                    return { label: OfferService.getMilestoneStatusLabel(milestone.status || 'PLANNED'), bg: '#f0f0f0', text: '#666' };
                  };
                  const statusDisplay = getStatusDisplay();
                  const canCreateInvoice = !milestone.invoice_id && isAccepted && (milestone.status === 'PLANNED' || milestone.status === 'READY_FOR_INVOICE');

                  return (
                    <tr key={milestone.id || milestone.sequence} className={styles.tableRow}>
                      <td className={styles.td}>{milestone.sequence}</td>
                      <td className={styles.tdBold} style={{ color: '#021a4a' }}>{milestone.label}</td>
                      <td className={styles.td}><span style={{ fontSize: '12px', color: '#666' }}>{OfferService.getMilestoneTypeLabel(milestone.milestone_type)}</span></td>
                      <td className={styles.tdRight}>{milestone.percentage}%</td>
                      <td className={styles.tdBoldRight}>{milestone.amount_gross != null ? OfferService.formatCurrency(milestone.amount_gross) : '-'}</td>
                      <td className={styles.td} style={{ position: 'relative' }}>
                        {milestone.status && !milestone.invoice_id && milestone.status !== 'INVOICED' && milestone.status !== 'PAID' ? (
                          <div style={{ position: 'relative' }}>
                            <button onClick={() => setStatusDropdownMilestoneId(statusDropdownMilestoneId === milestone.id ? null : (milestone.id ?? null))} disabled={changingStatus} className={styles.milestoneStatusBadge} style={{ background: statusDisplay.bg, color: statusDisplay.text, cursor: changingStatus ? 'wait' : 'pointer' }}>
                              {statusDisplay.label} <FaChevronDown size={8} />
                            </button>
                            {statusDropdownMilestoneId === milestone.id && (<>
                              <div className={styles.milestoneStatusOverlay} onClick={() => setStatusDropdownMilestoneId(null)} />
                              <div className={styles.milestoneStatusDropdown}>
                                {(['PLANNED', 'READY_FOR_INVOICE', 'CANCELLED'] as const).map((status) => {
                                  const isCurrentStatus = milestone.status === status;
                                  const label = status === 'READY_FOR_INVOICE' ? 'Ready for invoice' : status === 'CANCELLED' ? 'Cancelled' : 'Planned';
                                  return (<button key={status} onClick={() => { if (!isCurrentStatus && milestone.id) handleChangeMilestoneStatus(milestone.id, status); }} className={styles.milestoneStatusItem} style={{ background: isCurrentStatus ? '#f3f4f6' : 'white', cursor: isCurrentStatus ? 'default' : 'pointer' }}>
                                    <span className={styles.statusDot} style={{ background: '#f0f0f0', border: '2px solid #666' }} />{label}{isCurrentStatus && <FaCheck size={10} color="#666" style={{ marginLeft: 'auto' }} />}
                                  </button>);
                                })}
                              </div>
                            </>)}
                          </div>
                        ) : milestone.status ? (<span className={styles.milestoneStatusBadge} style={{ background: statusDisplay.bg, color: statusDisplay.text }}>{statusDisplay.label}</span>) : null}
                      </td>
                      <td className={styles.td}>{milestone.invoice_id ? (<button onClick={() => navigate(`/invoices/${milestone.invoice_id}`)} className={styles.invoiceLinkBtn}>{milestone.invoice_number || 'View'}</button>) : (<span className={styles.invoiceDash}>--</span>)}</td>
                      <td className={styles.tdCenter}>
                        {canCreateInvoice ? (<button onClick={() => handleOpenMilestoneInvoiceModal(milestone)} className={styles.createInvoiceBtn}><FaFileInvoiceDollar size={10} /> Rechnung erstellen</button>)
                        : isDraft ? (<button onClick={() => navigate(`/invoices/${milestone.invoice_id}`)} className={styles.sendInvoiceBtn}><FaPaperPlane size={10} /> Rechnung senden</button>) : null}
                      </td>
                      <td className={styles.tdCenter}><button onClick={() => { setTasksMilestone(milestone); setShowMilestoneTasksModal(true); }} title="Leistungen & Subtasks anzeigen" className={styles.tasksBtn}><FaTasks size={14} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PdfDrawer isOpen={showPdfDrawer} onClose={() => setShowPdfDrawer(false)} pdfBlobUrl={pdfBlobUrl} onRefresh={handleLoadPdf} loading={loadingPdf} offerNumber={offer.offer_number} />
      <SendOfferEmailModal isOpen={showEmailModal} offer={offer} partnerEmail={partner?.email || ''} onClose={() => setShowEmailModal(false)} onSent={() => { setShowEmailModal(false); loadOffer(); }} />
      <CreateInvoiceFromOfferModal isOpen={showInvoiceModal} offer={offer} defaultKind={invoiceModalKind} defaultDepositPercent={50} onClose={() => setShowInvoiceModal(false)} onCreated={() => { setShowInvoiceModal(false); loadOffer(); }} />
      <CreateServiceContractModal isOpen={showServiceContractModal} onClose={() => setShowServiceContractModal(false)} offerId={offer.id} offerTitle={offer.title || ''} customerName={partner ? BusinessPartnerService.getDisplayName(partner) : ''} offerTotalGross={offer.total_gross || 0} onCreated={loadOffer} />
      <AcceptOfferModal isOpen={showAcceptModal} offerNumber={offer.offer_number} onClose={() => setShowAcceptModal(false)} onAccept={handleAcceptWithNote} loading={actionLoading} />
      <OrderConfirmationModal isOpen={showOrderConfirmationModal} offer={offer} partnerEmail={partner?.email || ''} onClose={() => setShowOrderConfirmationModal(false)} onSent={() => { setShowOrderConfirmationModal(false); loadOffer(); }} />
      <MilestoneInvoiceModal isOpen={showMilestoneInvoiceModal} milestone={selectedMilestone} partnerEmail={partner?.email || ''} onClose={() => { setShowMilestoneInvoiceModal(false); setSelectedMilestone(null); }} onCreated={() => { setShowMilestoneInvoiceModal(false); setSelectedMilestone(null); loadOffer(); }} />
      <MilestoneTasksModal isOpen={showMilestoneTasksModal} milestone={tasksMilestone} lineItems={offer?.line_items} onClose={() => { setShowMilestoneTasksModal(false); setTasksMilestone(null); }} onTasksChanged={loadOffer} />

      {showOpenTasksWarning && pendingInvoiceMilestone && (
        <div className={styles.warningOverlay} onClick={() => { setShowOpenTasksWarning(false); setPendingInvoiceMilestone(null); }}>
          <div className={styles.warningDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.warningHeader}><FaExclamationTriangle size={20} color="#f59e0b" /><h3 className={styles.warningTitle}>Open Tasks</h3></div>
            <div className={styles.warningBody}>
              <p className={styles.warningText}>There are still <strong>{pendingInvoiceMilestone.tasks_open}</strong> open task(s) for this milestone.</p>
              <p className={styles.warningSubtext}>Do you still want to create the invoice?</p>
              <div className={styles.warningActions}>
                <button onClick={() => { setShowOpenTasksWarning(false); setPendingInvoiceMilestone(null); }} className={styles.cancelBtn}>Cancel</button>
                <button onClick={handleConfirmInvoiceWithOpenTasks} className={styles.confirmWarningBtn}>Yes, create invoice</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {scopeDrawerLine && (<OfferLineScopeDrawer offerLineId={scopeDrawerLine.id} lineName={scopeDrawerLine.name} isOpen={scopeDrawerOpen} onClose={() => { setScopeDrawerOpen(false); setScopeDrawerLine(null); }} />)}
    </div>
  );
}
